import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ArticleCard, { categoryName } from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import SubscribeSection from '@/pages/home/components/SubscribeSection';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <title>瓜料不见了 | 糖心吃瓜</title>
        <SiteHeader />
        <main className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl md:text-3xl text-foreground-950">这条瓜料好像被吃掉了</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经下架，或者链接输入有误。回瓜田速递看看别的甜味瓜吧。</p>
          <Link
            to="/fresh"
            className="mt-7 rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回瓜田速递
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const fallbackRelated = articles.filter((a) => a.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://lhyf56.cn/' },
          { '@type': 'ListItem', position: 2, name: '瓜田速递', item: 'https://lhyf56.cn/fresh' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://lhyf56.cn/article/${article.slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Organization', name: article.author },
        publisher: { '@type': 'Organization', name: '糖心吃瓜' },
        articleSection: categoryName(article.category),
        keywords: article.tags.join(','),
        inLanguage: 'zh-CN',
      },
    ],
  };

  return (
    <>
      <title>{`${article.title} | 糖心吃瓜`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://lhyf56.cn/article/${article.slug}`} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={`https://lhyf56.cn/article/${article.slug}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <SiteHeader />
      <main className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/fresh" className="hover:text-primary-600 transition-colors cursor-pointer">瓜田速递</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link
              to={`/fresh?category=${article.category}`}
              className="hover:text-primary-600 transition-colors cursor-pointer"
            >
              {categoryName(article.category)}
            </Link>
          </nav>

          <article className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
              <i className="ri-heart-3-line" aria-hidden="true"></i>
              {categoryName(article.category)}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl leading-snug text-foreground-950">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-smile-line text-primary-400" aria-hidden="true"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                阅读约 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-primary-400" aria-hidden="true"></i>
                {article.heat} 人围观
              </span>
            </div>

            <div className="mt-6 w-full h-56 md:h-[380px] overflow-hidden rounded-xl bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 糖心吃瓜`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="mt-7 text-base md:text-lg text-foreground-800 leading-relaxed">{article.excerpt}</p>

            {article.quote && (
              <blockquote className="mt-6 rounded-xl border-l-4 border-primary-400 bg-accent-50 px-5 py-4 font-heading text-lg text-foreground-900">
                {article.quote}
              </blockquote>
            )}

            <div className="mt-8 flex flex-col gap-8">
              {article.content.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950">{section.heading}</h2>
                  <div className="mt-4 flex flex-col gap-4">
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 12)} className="text-sm md:text-base text-foreground-700 leading-loose">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                  #{t}
                </span>
              ))}
            </div>
          </article>
        </div>

        <section className="mt-14 max-w-5xl mx-auto">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">顺着甜度继续看</h2>
            <Link
              to="/fresh"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              更多瓜料
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {relatedList.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      </main>

      <SubscribeSection />
      <SiteFooter />
      <BackToTop />
    </>
  );
}